import { buildToothPalette } from '../../utilities/toothShading';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Shade used for selected teeth when the formula line has no color yet. */
const NATURAL_SHADE = '#f4eee2';
/** Cool blue, far from any dental shade, so selected teeth stand out. */
const IDLE_SHADE = '#8aaee0';

const LABEL_COLOR = '#000000';
const LABEL_HOVER_COLOR = '#1693A5';
const HOVER_STROKE = '#1693A5';

const svgEl = (name, attrs = {}, children = []) => {
    const node = document.createElementNS(SVG_NS, name);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, String(value)));
    children.forEach((child) => node.appendChild(child));
    return node;
};

const gradientStops = (stops) => stops.map(([offset, color, opacity = 1]) => svgEl('stop', {
    offset,
    'stop-color': color,
    'stop-opacity': opacity,
}));

const enamelGradient = (id, palette) => svgEl('radialGradient', { id, cx: '40%', cy: '34%', r: '72%' }, gradientStops([
    ['0%', palette.highlight],
    ['35%', palette.light],
    ['68%', palette.base],
    ['92%', palette.shade],
    ['100%', palette.edge],
]));

/**
 * Restyles a Raphael tooth chart with shaded enamel on a transparent background;
 * selected teeth are rendered in `color`.
 */
export const applyRealisticSkin = (paper, { teeth, labels, color, idPrefix }) => {
    const svg = paper.canvas;
    const defs = paper.defs;
    const prefix = String(idPrefix).replace(/[^\w-]/g, '_');
    const ids = {
        selected: `${prefix}-rs-enamel-selected`,
        idle: `${prefix}-rs-enamel-idle`,
        shadow: `${prefix}-rs-shadow`,
    };
    const palettes = {
        selected: buildToothPalette(color || NATURAL_SHADE),
        idle: buildToothPalette(IDLE_SHADE),
    };

    defs.appendChild(enamelGradient(ids.selected, palettes.selected));
    defs.appendChild(enamelGradient(ids.idle, palettes.idle));
    defs.appendChild(svgEl('filter', { id: ids.shadow, x: '-30%', y: '-30%', width: '160%', height: '160%' }, [
        svgEl('feDropShadow', {
            dx: 0.6,
            dy: 1.2,
            stdDeviation: 1.1,
            'flood-color': '#000000',
            'flood-opacity': 0.3,
        }),
    ]));

    const toothNodes = new Map();
    Object.entries(teeth).forEach(([num, tooth]) => {
        if (tooth?.node) {
            toothNodes.set(tooth.node, Number(num));
        }
    });

    // Groove overlays are drawn right after their tooth, so DOM order maps them back.
    const grooves = {};
    let currentTooth = null;
    Array.from(svg.querySelectorAll('path')).forEach((node) => {
        if (toothNodes.has(node)) {
            currentTooth = toothNodes.get(node);
            grooves[currentTooth] = [];
            return;
        }
        if (currentTooth != null) {
            grooves[currentTooth].push(node);
        }
    });

    Object.values(labels).forEach((label) => {
        label?.attr?.({ fill: LABEL_COLOR });
    });

    const selectedTeeth = new Set();

    const paint = (num, isSelected) => {
        const tooth = teeth[num];
        if (!tooth?.node) {
            return;
        }
        const palette = isSelected ? palettes.selected : palettes.idle;
        if (isSelected) {
            selectedTeeth.add(num);
        } else {
            selectedTeeth.delete(num);
        }
        tooth.node.setAttribute('fill', `url(#${isSelected ? ids.selected : ids.idle})`);
        tooth.node.setAttribute('stroke', palette.edge);
        tooth.node.setAttribute('stroke-width', '0.8');
        tooth.node.setAttribute('filter', `url(#${ids.shadow})`);
        (grooves[num] || []).forEach((node) => {
            node.setAttribute('stroke', palette.groove);
            node.setAttribute('stroke-opacity', '0.55');
            node.setAttribute('stroke-width', '0.55');
            node.setAttribute('stroke-linecap', 'round');
            node.setAttribute('stroke-linejoin', 'round');
            node.setAttribute('fill', 'none');
        });
    };

    const hover = (tooth, isHovering) => {
        const num = tooth?.node ? toothNodes.get(tooth.node) : undefined;
        if (num == null) {
            return;
        }
        const palette = selectedTeeth.has(num) ? palettes.selected : palettes.idle;
        tooth.node.setAttribute('stroke', isHovering ? HOVER_STROKE : palette.edge);
        tooth.node.setAttribute('stroke-width', isHovering ? '1.6' : '0.8');
        labels[num]?.attr?.({
            fill: isHovering ? LABEL_HOVER_COLOR : LABEL_COLOR,
            'font-weight': isHovering || selectedTeeth.has(num) ? 'bold' : 'normal',
        });
    };

    Object.keys(teeth).forEach((num) => paint(Number(num), false));

    return { paint, hover };
};
